import {action, makeAutoObservable, observable, toJS} from 'mobx'
import {ArrUtil, HTMLUtil, ObjUtil, Util} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerEntry,
  Hub,
  Subtask,
  SubtaskList,
  User
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {parentNavigationStore} from '@components/right-panel/checklists-tab/subtasks-new/parent-tasks/parent-navition-store'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {SeenBy} from '@extensions/seen-by'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'

export type NumericStickersSum = Map<
  DataObjectId,
  {
    value: number
    title: string
  }
>

export class SubtasksStore {
  state = {
    newTaskTitle: '',
    dragState: false,
    showTree: false,
    showNewTaskInput: false,
    treeHeight: 0,
    treeUp: false,
    addSubtaskShown: false,
    selectedTasks: [] as DataObjectId[],
    open: false
  }

  data: {list: SubtaskList; parents: Hub[]} = {
    list: {order: [], subtasks: {}},
    parents: []
  }

  constructor(public taskId: DataObjectId) {
    makeAutoObservable(
      this,
      {
        data: observable.shallow,
        setOrder: action.bound,
        setDragState: action.bound,
        onBeforeOpenChat: action.bound
      },
      {autoBind: true}
    )
    this.syncState()
    this.state.open = !this.subtasks.length
  }

  get task(): Hub | undefined {
    return Task.getById(this.taskId)
  }

  get parentTasksCount(): number {
    return SubtaskUtil.getParentTaskIds(this.taskId).length
  }

  onAfterTitleUpdate(taskId: DataObjectId, title: string): void {
    const subtasks = ObjUtil.update(
      this.data.list.subtasks,
      taskId,
      (x: Subtask) => ({...x, title})
    )
    this.data.list = ObjUtil.update(this.data.list, 'subtasks', () => subtasks)
    this.commitSubtaskList()
  }

  getSubtask(taskId: DataObjectId): Subtask | undefined {
    return this.subtasks.find(x => x.id === taskId)
  }

  setDragState(dragTaskId: DataObjectId | undefined): void {
    DragData.setCurrent(
      dragTaskId ? {type: 'task', id: dragTaskId} : {type: 'empty'}
    )
    this.state.dragState = !!dragTaskId
  }

  get subtasks(): Subtask[] {
    return SubtaskUtil.getSubtasksFromList(this.data.list)
  }

  get subtasksAsHub(): Hub[] {
    return SubtaskUtil.getSubtasksFromListAsHub(this.data.list)
  }

  get doneCount(): number {
    return this.subtasks.filter(x => x.isCompleted).length
  }

  get allCompleted(): boolean {
    return this.doneCount === this.subtasks.length
  }

  get allUsers(): User[] {
    const box = Sync.getCurrentSandbox()
    return ArrUtil.unique(
      this.subtasks.flatMap(x =>
        Sticker.getUsers(SubtaskUtil.getStickerHub(x))
      ),
      x => x
    )
      .map(id => box.getUser(id))
      .filter((x): x is User => !!x)
  }

  get deadlines(): {
    deadline: DeadlineStickerEntry
    completed: boolean
  }[] {
    return this.subtasks
      .map(
        x =>
          x.stickers && {
            deadline: Sticker.getDeadline(x)?.current,
            completed: x.isCompleted
          }
      )
      .filter(Boolean)
  }

  get numericStickersSum(): NumericStickersSum {
    const box = Sync.getCurrentSandbox()
    const stickersSum = new Map()

    this.subtasks.forEach(subtask => {
      const subtaskHub = box.getHub(subtask.id)
      const stickers = (subtaskHub && Sticker.getAllPinned(subtaskHub)) || []

      stickers.forEach(sticker => {
        if (Sticker.getType(sticker) === 'numeric') {
          stickersSum.set(sticker.id, {
            title: sticker.title,
            value:
              (stickersSum.get(sticker.id)?.value || 0) +
              Number(subtask.stickers[sticker.id])
          })
        }
      })
    })

    return stickersSum
  }

  onBeforeOpenChat(): void {
    parentNavigationStore.addParent(this.taskId)
  }

  setShowTree(showTree: boolean): void {
    this.state.showTree = showTree
    this.state.selectedTasks = this.subtasks.map(x => x.id)
  }

  setShowNewTaskInput(showNewTaskInput: boolean): void {
    this.state.showNewTaskInput = showNewTaskInput
  }

  setSelectedTasks(tasks: DataObjectId[]): void {
    this.state.selectedTasks = tasks
  }

  setOrder(order: DataObjectId[]): void {
    const subtaskList = {...this.data.list, order: order}
    this.data.list = subtaskList
    this.commitSubtaskList()
  }

  get canEdit(): boolean {
    const task = Task.getById(this.taskId)
    return !!task && PermissionsChecker.checkEditSubtasks(task)
  }

  removeSubtasks(subtaskIds: DataObjectId[]): void {
    this.data.list = SubtaskUtil.deleteSubtask(this.data.list, subtaskIds)
    this.commitSubtaskList()
  }

  syncState(): void {
    const list = SubtaskUtil.getSubtaskList(this.task)
    const subtaskIds = new Set(
      SubtaskUtil.getSubtasksFromList(list).map(x => x.id)
    )
    this.data.list = {
      order: list.order.filter(x => subtaskIds.has(x)),
      subtasks: ObjUtil.filter(list.subtasks, k =>
        subtaskIds.has(k as DataObjectId)
      ) as Record<DataObjectId, Subtask>
    }
    this.data.parents = SubtaskUtil.getParentTaskIds(this.taskId)
      .map(Task.getById)
      .filter(Util.isDefined)
      .filter(x => !x.deleted)
  }

  setTreeHeight(height: number, treeUp: boolean): void {
    this.state.treeHeight = height
    this.state.treeUp = treeUp
  }

  setNewTaskTitle(title: string): void {
    this.state.newTaskTitle = title
  }

  toggleOpen(open?: boolean): void {
    open = typeof open === 'undefined' ? !this.state.open : open
    if (!this.state.open && open) {
      void Tracking.send(TrackingEvent.subtasksOpenList)
    }
    this.state.open = open
  }

  setAddSubtaskShown(shown: boolean): void {
    this.state.addSubtaskShown = shown
    if (shown) {
      this.state.newTaskTitle = ''
      this.state.showTree = false
    }
  }

  saveSelectedTasks(): void {
    const box = Sync.getCurrentSandbox()
    const oldTasks = this.subtasks.map(x => x.id)
    const newTasks = this.state.selectedTasks
    const {add, remove} = ArrUtil.diff(oldTasks, newTasks)
    this.addSubtasks(add.map(x => box.getHub(x)).filter(Util.isDefined))
    this.setAddSubtaskShown(false)
    this.removeSubtasks(remove)
    void Tracking.send(TrackingEvent.subtasksAddExistent)
  }

  commitSubtaskList(): void {
    this.task && SubtaskUtil.setSubtaskList(this.task, toJS(this.data.list))
  }

  createNewTask(): void {
    const title = HTMLUtil.trimLineBreaks(this.state.newTaskTitle)
    this.state.newTaskTitle = ''
    if (!this.task || !title) {
      return
    }
    const task = Task.createTask(title, {
      isPrivate: !!this.task.data.private,
      allowApi: true
    })

    if (task) {
      if (!SeenBy.isSeen(task, globals.currentUserId)) {
        SeenBy.markSeen(task, globals.currentUserId)
      }

      this.addSubtasks([task])
      void Tracking.send(TrackingEvent.subtasksCreateNew)
    }
  }

  addSubtasks(tasks: Hub[]): void {
    this.data.list = SubtaskUtil.addSubtasks(this.data.list, tasks)
    this.commitSubtaskList()
  }
}

const [StoreContext, useStore] = createUseStore(SubtasksStore)
export {StoreContext, useStore}
